import { useEffect, useRef } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  EVOTRUX_BLUE,
  EVOTRUX_BLUE_LIGHT,
  EVOTRUX_BORDER_GREY_HOVER,
} from "constants/index";
import { getEquipmentTypesDisplay, getServiceTypeDisplayLabel } from "utils";
import { getTemplateRouteLabel } from "utils/templates";

import {
  getShipmentTemplateVisibiliyLabel,
  getTemplateSharerBadge,
} from "./utils";

import type { ShipmentTemplate } from "types/shipment";

const HIGHLIGHT_IN_MS = 150;
const HIGHLIGHT_FADE_OUT_MS = 800;

interface Props {
  template: ShipmentTemplate;
  selectedTemplateId: number | null;
  highlighted?: boolean;
  onDeleteTemplate: (template: ShipmentTemplate) => void;
  onShareTemplate: (template: ShipmentTemplate) => void;
  liProps: React.HTMLAttributes<HTMLLIElement>;
}

export function ShipmentTemplateListElement({
  template,
  selectedTemplateId,
  highlighted = false,
  onDeleteTemplate,
  onShareTemplate,
  liProps,
}: Props) {
  const routeLabel = getTemplateRouteLabel(template);
  const visibilityLabel = getShipmentTemplateVisibiliyLabel(template);
  const sharerBadge = getTemplateSharerBadge(template);
  const equipmentTypes = template.content?.equipment_types ?? [];
  const service = template.content?.shipment?.service;

  const elementRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (highlighted) {
      elementRef.current?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlighted]);

  const getBackgroundColor = () => {
    if (highlighted) return EVOTRUX_BLUE_LIGHT;
    return selectedTemplateId === template.id ? "action.selected" : undefined;
  };

  return (
    <Box
      component="li"
      {...liProps}
      ref={elementRef}
      sx={{
        display: "block !important",
        py: 1.5,
        px: 2,
        bgcolor: getBackgroundColor(),
        // The highlight snaps on and fades out slowly once it is removed.
        transition: `background-color ${highlighted ? HIGHLIGHT_IN_MS : HIGHLIGHT_FADE_OUT_MS}ms ease-out`,
        cursor: "pointer",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Stack flex={1} spacing={0.5} minWidth={0}>
          <Typography variant="subtitle2" fontWeight={700}>
            {template.name}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            flexWrap="wrap"
          >
            {routeLabel && (
              <Typography variant="body2" color="text.secondary">
                {routeLabel}
              </Typography>
            )}
            {equipmentTypes.map((type) => (
              <Chip
                key={type}
                label={getEquipmentTypesDisplay([type])}
                size="small"
              />
            ))}
            {service && (
              <Chip label={getServiceTypeDisplayLabel(service)} size="small" />
            )}
            {visibilityLabel && <Chip label={visibilityLabel} size="small" />}
          </Stack>
        </Stack>
        {sharerBadge && (
          <Tooltip title={sharerBadge.tooltip} placement="top" arrow>
            <Avatar
              role="img"
              sx={{
                width: (theme) => theme.spacing(3.5),
                height: (theme) => theme.spacing(3.5),
                typography: "caption",
                fontWeight: "fontWeightMedium",
                bgcolor: EVOTRUX_BLUE_LIGHT,
                color: EVOTRUX_BLUE,
              }}
            >
              {sharerBadge.initials}
            </Avatar>
          </Tooltip>
        )}
        <Tooltip title="Share template">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onShareTemplate(template);
            }}
            aria-label="Share template"
            sx={{
              color: EVOTRUX_BORDER_GREY_HOVER,
              "&:hover": { color: EVOTRUX_BLUE },
            }}
          >
            <ShareOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete template">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteTemplate(template);
            }}
            aria-label="Delete template"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Divider sx={{ mt: 1.5 }} />
    </Box>
  );
}
